// The mold assignment: which keycaps go on which run of a profile's molds. One run is one
// mold filled once, i.e. one printing file and one transfer film. ProcessOrderScreen runs
// it over an order's designs to build the printing files, LayoutEditScreen over a layout
// to work out the transfer film quantity of each profile (the number of runs). matchKeycapToSlot,
// whether a keycap fits a slot, came from generalHelpers.js in PR 25 (its last function). Since
// chain 9 (Oct 2026) also what the two screens make of the molds route's documents and of the
// assignment: the mold caches both keep (a copy each), the printing settings of each mold, a
// design with its printing runs and the transfer films each profile of a layout needs.
import { converter } from 'culori';
import { printingCopies } from './storedDesigns';

const toOklch = converter('oklch'); // converts hex colors into oklch, which is better for visual lightness sorting than rgb

export function getKeycapLightness(color) {
  const fallbackColor = '#FFFFFF'; // uses white when the keycap has no valid color value
  const parsedColor = toOklch(color || fallbackColor); // converts the keycap color into oklch values

  if (!parsedColor) {
    return 1; // treats invalid colors as white so they are placed with the light colors
  }

  return parsedColor.l; // returns the perceptual lightness value, where 1 is light and 0 is dark
} // extracts the perceptual lightness of a keycap color so colors can be sorted visually from light to dark

export function compareKeycapsByLightness(a, b) {
  const lightnessA = getKeycapLightness(a.color); // gets the lightness of the first keycap color
  const lightnessB = getKeycapLightness(b.color); // gets the lightness of the second keycap color

  return lightnessB - lightnessA; // sorts from light to dark, so white comes before gray, red, and black
} // compares two keycap colors only by lightness because this produced the best practical sorting result. It parses both colors on every comparison: a few milliseconds per design, once per order load

export function matchKeycapToSlot(keycap, slot, profile) {
  // Extract boolean properties or default them to false if not defined:
  const keycapIsConvex = keycap.isConvex ?? false;
  const slotIsConvex = slot.isConvex ?? false;
  const keycapIsIsoEnter = keycap.isIsoEnter ?? false;
  const slotIsIsoEnter = slot.isIsoEnter ?? false;
  const keycapStepped = keycap.stepped ?? false;
  const slotStepped = slot.stepped ?? false;

  if (keycapIsIsoEnter) {
    return slotIsIsoEnter === true; // If the keycap is ISO enter, it must be assigned to an ISO enter slot only
  }

  // Determine if rows need to match (only compare row if the profile is not XDA (as it is a uniform profile); if profile is XDA, rowsMatch is always true):
  const keycapRow = keycap.row ?? null;
  const slotRow = slot.row ?? null;
  const rowsMatch = profile === 'XDA' ? true : keycapRow === slotRow;

  let keycapWidth = keycap.width;
  let keycapHeight = keycap.height;

  // Special handling for XDA (uniform) profile (If keycap is 1x2, treat it as 2x1 for slot matching, as they are both the same keycaps for uniform profiles):
  if (profile === 'XDA' && keycapWidth === 1 && keycapHeight === 2) {
    keycapWidth = 2;
    keycapHeight = 1;
  }

  // Return true only if all conditions match:
  return (
    keycapWidth === slot.width &&
    keycapHeight === slot.height &&
    keycapIsConvex === slotIsConvex &&
    keycapStepped === slotStepped &&
    rowsMatch
  );
}

export function assignKeycapsToMolds(
  keycapData,
  moldsArray,
  profile,
  copies = 1
) {
  const baseKeycaps = keycapData
    .flat()
    .filter((keycap) => !keycap.isSpacer)
    .sort(compareKeycapsByLightness); // flattens the 2d keycap data, removes spacers, and always sorts colors from light to dark

  // duplicate the keycap set as many times as needed (based on copies, which is the order item's quantity when the layout allows transfer film pooling):
  let unassignedKeycaps = [];
  for (let instance = 0; instance < copies; instance++) {
    unassignedKeycaps.push(...baseKeycaps.map((keycap) => ({ ...keycap }))); // every copy is its own object, so the ones a run takes can be told apart from the rest
  }

  const assignedMoldRuns = []; // this will hold arrays of keycaps assigned to each "mold run" (one array for every new mold that is needed)

  for (const { id: moldDocId, mold: currentMold } of moldsArray) {
    // go through every mold document for this profile. A mold is filled run after run before the next mold is tried, in the order the molds route sends them (unsorted): the molds of a profile hold different keycap types, so no keycap could go on either of them, and the order only numbers the printing files (user decision, Oct 2026)
    while (unassignedKeycaps.length > 0) {
      // keep using the current mold as long as keycaps still fit on it:
      const runAssignment = []; // stores the keycaps assigned to this mold run, each with the geometry of its slot
      const slotStates = currentMold.map(() => false); // tracks which mold slots are already occupied
      const assignedKeycaps = new Set(); // the keycaps that got a slot in this mold run

      for (const keycap of unassignedKeycaps) {
        // go through every keycap that still needs a mold slot:
        let assigned = false; // tracks whether this keycap was assigned in this mold run

        for (
          let slotIndex = 0;
          slotIndex < currentMold.length && !assigned;
          slotIndex++
        ) {
          // go through mold slots until the keycap fits or no slots are left:
          if (slotStates[slotIndex]) continue; // skip slots that are already occupied

          const slot = currentMold[slotIndex];

          if (matchKeycapToSlot(keycap, slot, profile)) {
            // if the current keycap fits the current mold slot:
            slotStates[slotIndex] = true; // mark the slot as occupied

            runAssignment.push({
              ...keycap,
              w: slot.w,
              h: slot.h,
              xCenter: slot.xCenter,
              yCenter: slot.yCenter,
              adjustments: slot.adjustments,
            }); // add the keycap to this mold run, placed where its slot is

            assignedKeycaps.add(keycap);
            assigned = true;
          }
        }
      }

      if (assignedKeycaps.size === 0) break; // stop using this mold if none of the remaining keycaps fit

      unassignedKeycaps = unassignedKeycaps.filter(
        (keycap) => !assignedKeycaps.has(keycap)
      ); // remove assigned keycaps from the list of remaining keycaps

      assignedMoldRuns.push({ moldDocId, keycaps: runAssignment }); // store this mold run along with the _id of the mold document
    }

    if (unassignedKeycaps.length === 0) break; // stop if all keycaps were assigned to mold slots
  }

  return { moldRuns: assignedMoldRuns, unassignedKeycaps };
} // assigns the keycaps to mold slots, run by run, and returns the runs and the keycaps no slot fits

export const moldSlots = (molds) =>
  molds.map(({ _id, mold }) => ({ id: _id, mold })); // the molds of a profile as the assignment takes them: each document GET /api/molds/profile/:profile sends, reduced to its id and slots (ProcessOrderScreen's and LayoutEditScreen's mold caches held a copy each until chain 9)

export const moldPrintSettings = (molds) =>
  Object.fromEntries(
    molds.map(({ _id, scaling, punchingHoleXAdjustments }) => [
      _id,
      { scaling, punchingHoleXAdjustments },
    ])
  ); // what the printing files need of each mold beside its slots, by document id: the scaling and the punching holes' adjustments (ProcessOrderScreen's moldDocsById)

export const designWithPrintingRuns = (
  design,
  layoutDoc,
  orderItem,
  profileToMoldsMap
) => {
  const profile = design.profileEditor.profile; // every stored design has one: the model has required it since the Dec 2024 schema, and no document from before is left (checked by query, Oct 2026; a branch for a design without a profile went in chain 9, user decision)
  const { poolingEnabled: allowPooling, copies } = printingCopies(
    layoutDoc,
    orderItem
  ); // whether the layout's transfer film may be pooled, and then the order item's quantity as the copies the mold assignment lays out at once

  const moldsArray = profileToMoldsMap[profile]; // get all the molds for the designs keycap profile: the loader fetched every profile's before, an empty list for one without active molds

  // Assign keycaps to molds:
  const { moldRuns, unassignedKeycaps } = assignKeycapsToMolds(
    design.keycapData,
    moldsArray,
    profile,
    copies
  );

  return {
    design: {
      ...design,
      poolingEnabled: allowPooling,
      moldAssignments: moldRuns,
    },
    unassignedKeycaps,
  }; // return the design with a new moldAssignments field, which holds one or multiple arrays of keycaps (each array represents one printing SVG file)
}; // an order's design with its printing runs, as ProcessOrderScreen builds the printing files: the design's keycaps laid out on the molds of its profile (the order item's quantity at once when the layout pools its film), and the keycaps no slot took, which the screen warns about

export const transferFilmQuantities = (layout, profiles, profileToMoldsMap) => {
  const quantitiesByProfile = {}; // stores the calculated transfer film quantity for each profile
  const profilesWithUnassignedKeycaps = []; // the profiles whose molds had no slot for some keycap

  profiles.forEach((profile) => {
    // go through every profile used by the layout:
    const moldsArray = profileToMoldsMap[profile]; // get all molds for the current profile: the editor's effect fetched every profile of the layout before, an empty list for one without active molds (a fallback for a missing entry went in chain 9: dead, a review)
    const { moldRuns, unassignedKeycaps } = assignKeycapsToMolds(
      layout,
      moldsArray,
      profile
    ); // calculate how many mold runs are needed for this profile

    quantitiesByProfile[profile] = moldRuns.length; // store the calculated transfer film quantity for this profile

    if (unassignedKeycaps.length > 0) {
      profilesWithUnassignedKeycaps.push(profile);
    }
  });

  return { quantitiesByProfile, profilesWithUnassignedKeycaps };
}; // the transfer films a layout needs in each of its profiles, one per mold run, as the layout editor works them out and saves them with the layout; and the profiles the editor warns about
